import gql from 'graphql-tag';
import i18next from '../i18n/config';
import { ErrorsAttributes } from '../constants';

export const CustomCheckCoreAttributesWithoutSites = `
id
accountId
parentCustomCheckId
name
notes
enabled
defaultEnabledForNewSites
category
categoryOverwritten
categoryOriginal
allTrue
shared
policySet {
  id
  name
}
showToContentEditors
showToContentEditorsOverwritten
showToContentEditorsOriginal
impact
successCriterionTags
rules {
  id
  type
  rule
  onlyCheckText
  ignoreCase
  contains
  urlOption
  includePartialMatches
}
`;

export const CustomCheckCoreAttributes = `
${CustomCheckCoreAttributesWithoutSites}
sites {
  id
  name
  url
  webGovernanceCheck
  a11yCheck
  bestPracticesCheck
  linkCheck
  profanityCheck
  seoCheck
  spellCheck
}
`;

export const CustomCheckQuery = gql`
  query CustomCheckQuery($customCheckId: String!, $accountId: String!) {
    customCheck(customCheckId: $customCheckId, accountId: $accountId) {
      ${CustomCheckCoreAttributes}
    }
  }
`;

export const CreateCustomCheckMutation = gql`
  mutation CreateCustomCheck($accountId: String!, $name: String!, $siteIds: [String!], $allTrue: Boolean!, $enabled: Boolean!, $defaultEnabledForNewSites: Boolean, $notes: String, $category: CustomCheckCategory, $showToContentEditors: Boolean, $impact: CustomCheckImpact, $successCriterionTags: [String!], $rules: [RuleInput!]!) {
    createCustomCheck(accountId: $accountId, name: $name, siteIds: $siteIds, allTrue: $allTrue, enabled: $enabled, defaultEnabledForNewSites: $defaultEnabledForNewSites, notes: $notes, category: $category, showToContentEditors: $showToContentEditors, impact: $impact, successCriterionTags: $successCriterionTags, rules: $rules) {
      customCheck {
        ${CustomCheckCoreAttributesWithoutSites}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const CopyCustomCheckDependenciesQuery = gql`
  query CopyCustomCheckDependenciesQuery($accountId: String!, $customCheckId: String!) {
    customCheck(accountId: $accountId, customCheckId: $customCheckId) {
      ${CustomCheckCoreAttributes}
    }
  }
`;

export const UpdateCustomCheckMutation = gql`
  mutation UpdateCustomCheck($accountId: String!, $customCheckId: String!, $name: String!, $siteIds: [String!], $allTrue: Boolean!, $enabled: Boolean!, $defaultEnabledForNewSites: Boolean, $notes: String, $category: CustomCheckCategory, $showToContentEditors: Boolean, $impact: CustomCheckImpact, $successCriterionTags: [String!], $rules: [RuleInput!]!) {
    updateCustomCheck(accountId: $accountId, customCheckId: $customCheckId, name: $name, siteIds: $siteIds, allTrue: $allTrue, enabled: $enabled, defaultEnabledForNewSites: $defaultEnabledForNewSites, notes: $notes, category: $category, showToContentEditors: $showToContentEditors, impact: $impact, successCriterionTags: $successCriterionTags, rules: $rules) {
      customCheck {
        ${CustomCheckCoreAttributesWithoutSites}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const UpdateSharedCustomCheckMutation = gql`
  mutation UpdateSharedCustomCheck($accountId: String!, $customCheckId: String!, $siteIds: [String!], $enabled: Boolean!, $defaultEnabledForNewSites: Boolean, $category: CustomCheckCategory, $categoryOverwritten: Boolean!, $showToContentEditors: Boolean, $showToContentEditorsOverwritten: Boolean!, $impact: CustomCheckImpact, $successCriterionTags: [String!]) {
    updateSharedCustomCheck(accountId: $accountId, customCheckId: $customCheckId, siteIds: $siteIds, enabled: $enabled, defaultEnabledForNewSites: $defaultEnabledForNewSites, category: $category, categoryOverwritten: $categoryOverwritten, showToContentEditors: $showToContentEditors, showToContentEditorsOverwritten: $showToContentEditorsOverwritten, impact: $impact, successCriterionTags: $successCriterionTags) {
      customCheck {
        ${CustomCheckCoreAttributesWithoutSites}
        ${ErrorsAttributes}
      }
    }
  }
`;

export const CustomChecksQuery = gql`
  query CustomChecksQuery($accountId: String!, $page: Int!, $perPage: Int!, $sortBy: CustomChecksSortBy!, $sortOrder: SortOrder!, $search: String, $includeShared: Boolean, $policySetId: String, $enabled: Boolean) {
    customChecks(accountId: $accountId, page: $page, perPage: $perPage, sortBy: $sortBy, sortOrder: $sortOrder, search: $search, includeShared: $includeShared, policySetId: $policySetId, enabled: $enabled) {
      currentPage
      perPage
      totalPages
      totalEntries
      nodes {
        ${CustomCheckCoreAttributesWithoutSites}
      }
    }
  }
`;

export const AllCustomChecksQuery = gql`
  query CustomChecksQuery($accountId: String!) {
    customChecks(accountId: $accountId, page: 1, perPage: 100, sortBy: name, sortOrder: asc, includeShared: true) {
      nodes {
        ${CustomCheckCoreAttributesWithoutSites}
      }
    }
  }
`;

// HACK: currently destroyCustomChecks only accepts a single customCheckId
export const DestroyCustomChecksMutation = gql`
  mutation DestroyCustomChecks($accountId: String!, $customCheckIds: [String!]!) {
    destroyCustomChecks(accountId: $accountId, customCheckIds: $customCheckIds) {
      status
    }
  }
`;

export const customCheckStateDefaults = {
  name: '',
  notes: '',
  enabled: true,
  defaultEnabledForNewSites: true,
  allTrue: false,
  showToContentEditors: true,
  rules: [],
  sites: [],
  category: "policy",
  impact: 'moderate',
  successCriterionTags: [],
};

export const customCheckRuleStateDefaults = {
  contains: false,
  ignoreCase: false,
  onlyCheckText: false,
  includePartialMatches: false,
  rule: '',
  type: 'text',
};

const POLICY_CATEGORIES = new Map([
  ["policy", { checkFlag: "webGovernanceCheck", checkName: i18next.t("common.web_governance") }],
  ["a11y", { checkFlag: "a11yCheck" }],
  ["best_practice", { checkFlag: "bestPracticesCheck", checkName: i18next.t("common.best_practices") }],
  ["bad_link", { checkFlag: "linkCheck" }],
  ["spelling", { checkFlag: "spellCheck" }],
  ["profanity", { checkFlag: "profanityCheck" }],
  ["seo_issue", { checkFlag: "seoCheck" }],
  ["info_search", { checkFlag: "infoSearchCheck" }],
]);

for (const [categoryKey, category] of POLICY_CATEGORIES) {
  category.label = i18next.t(`model.custom_check.category.${categoryKey}`);

  if (!category.checkName) {
    category.checkName = category.label;
  }
}

const findCategoryByKey = (key = 'policy') => {
  let normalizedKey = key;
  if (!key || key === 'uncategorized') {
    normalizedKey = 'policy';
  }
  return POLICY_CATEGORIES.get(normalizedKey);
};

export { POLICY_CATEGORIES, findCategoryByKey };
